/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {
    useState,
    useEffect,
    useRef,
    createContext,
    useContext,
    useCallback,
    useMemo,
} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {StorefrontPreview} from '@salesforce/commerce-sdk-react/components'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'

import {useQuery} from '@tanstack/react-query'
import {useAccessToken, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
//import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Chakra
import {Box, useDisclosure, useStyleConfig} from '@chakra-ui/react'
import {SkipNavLink, SkipNavContent} from '@chakra-ui/skip-nav'

// Contexts
import {CurrencyProvider} from '@salesforce/retail-react-app/app/contexts'

import {GigyaModalProvider} from '../../context/gigya-modal'
import {IntegrationsInitializer} from '../../context/integrations'

// Local Project Components
import OfflineBanner from '@salesforce/retail-react-app/app/components/offline-banner'
import OfflineBoundary from '@salesforce/retail-react-app/app/components/offline-boundary'
import ScrollToTop from '@salesforce/retail-react-app/app/components/scroll-to-top'
import Footer from '../../components/footer'
import CheckoutHeader from '../../pages/checkout/partials/checkout-header'
import SovraHeader from '../sovra-header'
import CorporateBenefitBanner from '../corporate-benefit-banner'
import StoreLocatorModal from '@salesforce/retail-react-app/app/components/store-locator-modal'
import MultisiteHeader from '../multisite-header'

// Hooks
import {AuthModal, useAuthModal} from '@salesforce/retail-react-app/app/hooks/use-auth-modal'
import {AddToCartModalProvider} from '../../hooks/use-add-to-cart-modal'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useUpdateShopperContext} from '../../hooks/use-update-shopper-context'
// import {useGetIntegrations} from '../../custom-api/hooks/use-get-integrations'
import useCorporateBanner from '../../hooks/use-corporate-banner'
// import useCountryBasketManagement from '../../hooks/use-country-basket-management'
import useAriaHiddenOnIframesAndDividers from '../../hooks/use-aria-hidden-iframes'

// Localization
import {IntlProvider} from 'react-intl'

// Others
import {watchOnlineStatus, isServer, isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {getTargetLocale, fetchTranslations} from '../../utils/locale'
import Digioh from '../digioh'
//import ProfityTracker from '../profity-tracker'
import GlobalGigyaModal from '../modals/global-gigya-modal'

import {THEME_COLOR, DEFAULT_LOCALE, ACTIVE_DATA_ENABLED} from '../../constants'

import {Helmet} from 'react-helmet'
import useUtils from '../../custom-hooks/use-utils-helper'
import useKlarna from '../../hooks/use-klarna'
import {CustomPreferencesProvider} from '../../context/preferences'
import GtmPageView from '../gtm-page-view'
import {BRAND_LABEL, BRAND_KEY, MAIN_DOMAIN, GTM_DOMAIN} from '../../brand'
import SnapEngage from '../snapEngage'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import DoiModal from '../modals/doi-modal'

import RecommendedPersonalizationProducts from '../recommended-personalization-products'
import SovendusLandingPageContainer from '../sovendus/sovendus-landing-page-container'
import {GlobalCustomPreferencesProvider} from '../../context/global-preferences'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import useMutedVideo from '../../hooks/use-muted-video'
import {useEvergageBeacon} from '../../custom-api/hooks/use-evergage-beacon'
import {windowGetDevice} from '../../custom-api/hooks/use-google-tag-manager'
import {
    get_layer_page_category_and_subcategory,
    retrieve_status_code_gtm,
    virtual_page_view_no_customer,
} from '../../custom-api/layers/default'
import {useLocaleCountry, useLocaleLanguage} from '../../hooks/use-locale-custom'
import useTrustArc from '../../custom-api/hooks/use-trustarc'

const HeaderRefContext = createContext()

export const useHeaderRef = () => {
    return useContext(HeaderRefContext)
}

const App = ({children}) => {
    const {getTokenWhenReady} = useAccessToken()

    const activeData = useActiveData()
    const location = useLocation()
    const authModal = useAuthModal()
    const {site, locale} = useMultiSite()

    const country = useLocaleCountry()
    const language = useLocaleLanguage()

    const {customParams} = getConfig()

    const {res} = useServerContext()
    const {buildLocalizedUrl, isSplashPage, localeIsPresentInUrl} = useMulticountryUtils()

    const isSplashPageCondition =
        (res?.locals?.ua?.isBot && !localeIsPresentInUrl()) || isSplashPage

    const [isOnline, setIsOnline] = useState(true)

    const {showCorporateBanner, exitSource} = useCorporateBanner()

    const styles = useStyleConfig('App')

    const headerRef = useRef(null)

    const currentPage = useUtils().currentPage(location.pathname)
    const {isComponentsLibrary, isCheckout, isPaperless, isProductDetail, isMaintenance} =
        currentPage

    const {onClose} = useDisclosure()
    const {
        isOpen: isOpenStoreLocator,
        //onOpen: onOpenStoreLocator,
        onClose: onCloseStoreLocator,
    } = useDisclosure()
    const targetLocale = getTargetLocale({
        getUserPreferredLocales: () => {
            // CONFIG: This function should return an array of preferred locales. They can be
            // derived from various sources. Below are some examples of those:
            //
            // - client side: window.navigator.languages
            // - the page URL they're on (example.com/en-GB/home)
            // - cookie (if their previous preference is saved there)
            //
            // If this function returns an empty array (e.g. there isn't locale in the page url),
            // then the app would use the default locale as the fallback.

            // NOTE: Your implementation may differ, this is just what we did.
            return [locale?.alias || locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n,
    })

    // If the translation file exists, it'll be served directly from static folder (and won't reach this code here).
    // However, if the file is missing, the App would render a 404 page.
    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(
        location?.pathname,
    )

    // Fetch the translation message data using the target locale.
    const {data: messages} = useQuery({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: () => {
            if (is404ForMissingTranslationFile) {
                // Return early to prevent an infinite loop
                // Otherwise, it'll continue to fetch the missing translation file again
                return {}
            }
            return fetchTranslations(targetLocale)
        },
        enabled: isServer,
    })

    const {l10n} = site
    // Get the current currency to be used through out the app
    const currency = locale.preferredCurrency || l10n.defaultCurrency

    // Handle creating a new basket if there isn't one already assigned to the current
    // customer.
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()

    const {showTrustarcOverlay, listenTrustarcCMAPi} = useTrustArc()

    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateCustomerForBasket = useShopperBasketsMutation('updateCustomerForBasket')

    useEffect(() => {
        // update the basket currency if it doesn't match the current locale currency
        if (basket?.currency && basket?.currency !== currency) {
            updateBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {currency},
            })
        }
    }, [basket?.currency])

    useEffect(() => {
        // update the basket customer email
        if (
            basket &&
            customer?.isRegistered &&
            customer?.email &&
            customer?.email !== basket?.customerInfo?.email
        ) {
            updateCustomerForBasket.mutate({
                parameters: {basketId: basket.basketId},
                body: {
                    email: customer.email,
                },
            })
        }
    }, [customer?.isRegistered, customer?.email, basket?.customerInfo?.email, location?.pathname])

    useEffect(() => {
        //setupTrustarcScript(language)
        showTrustarcOverlay()
        listenTrustarcCMAPi()

        window._appstartTime = Date.now()
        // Listen for online status changes.
        watchOnlineStatus((isOnline) => {
            setIsOnline(isOnline)
        })

        if (ACTIVE_DATA_ENABLED) {
            const script0 = document.createElement('script')
            script0.type = 'text/javascript'
            script0.src = getAssetUrl('static/head-active_data.js')
            script0.id = 'headActiveData'
            script0.defer = true
            document.head.appendChild(script0)

            const script1 = document.createElement('script')
            script1.type = 'text/javascript'
            script1.src = getAssetUrl('static/dwanalytics-22.2.js')
            script1.id = 'dwanalytics'
            script1.async = true
            script1.onload = trackPage
            document.head.appendChild(script1)

            const script2 = document.createElement('script')
            script2.type = 'text/javascript'
            script2.src = getAssetUrl('static/dwac-21.7.js')
            script2.id = 'dwac'
            script2.async = true
            document.head.appendChild(script2)
        }
    }, [])

    // Handle updating the shopper context
    useUpdateShopperContext()

    useEffect(() => {
        const handleDomContentLoaded = () => {
            // Automatically close the mobile navigation when the location path is changed.
            onClose()
        }

        if (document.readyState === 'complete') {
            handleDomContentLoaded()
        } else {
            window.addEventListener('DOMContentLoaded', handleDomContentLoaded)
        }

        return () => {
            window.removeEventListener('DOMContentLoaded', handleDomContentLoaded)
        }
    }, [location])

    const trackPage = () => {
        activeData.trackPage(site.id, locale.id, currency)
    }

    useEffect(() => {
        trackPage()

        if (location.hash) {
            window.scrollTo(
                0,
                document.querySelector(`[name=${location.hash.replace(/#/, '')}], ${location.hash}`)
                    ?.offsetTop,
            )
        }
    }, [location])

    const {klarnaClientId, hasKlarna} = useKlarna()

    //useGetIntegrations()

    useAriaHiddenOnIframesAndDividers()
    useMutedVideo()
    useEvergageBeacon()

    const onErrorIntl = useCallback((err) => {
        if (!messages) {
            // During the ssr prepass phase the messages object has not loaded, so we can suppress
            // errors during this time.
            return
        }
        if (err.code === 'MISSING_TRANSLATION') {
            // NOTE: Remove the console error for missing translations during development,
            // as we knew translations would be added later.
            /*
            logger.warn('Missing translation', {
                namespace: 'App.IntlProvider',
                additionalProperties: {
                    errorMessage: err.message,
                },
            })
            */
            return
        }
        if (locale?.alias !== 'be-by') {
            throw err
        }
    }, [])

    const {page_category, page_subcategory} = get_layer_page_category_and_subcategory(currentPage)

    const virtualUrl = `https://${customParams?.currentHost}${location?.pathname}${location?.search}`

    const getDeviceOnFirstLoad = (res) => {
        if (res?.locals?.ua?.isMobile) return 'm'
        else if (res?.locals?.ua?.isTablet) return 't'
        else if (res?.locals?.ua?.isDesktop) return 'd'
        else if (isServer) return 'd'
        else return windowGetDevice(window?.innerWidth)
    }

    const getStatusCodeForEvent = () => {
        if (res?.statusCode) return res.statusCode
        else if (isServer) return 200
        else return retrieve_status_code_gtm()
    }

    const eventWithoutUser = useMemo(() => {
        return JSON.stringify(
            virtual_page_view_no_customer({
                event: 'realPageview',
                virtualUrl,
                pageCountry: country,
                pageLanguage: language,
                device: getDeviceOnFirstLoad(res),
                pageCategory: page_category,
                pageSubCategory: page_subcategory,
                statusCode: getStatusCodeForEvent(),
            }),
        )
    }, [])

    return (
        <Box className="sf-app" {...styles.container}>
            <StorefrontPreview getToken={getTokenWhenReady}>
                <Helmet>
                    <html lang={locale?.id} />

                    <link rel="dns-prefetch" href="https://fonts.feefo.com" />
                    <link rel="dns-prefetch" href="https://storage.googleapis.com" />
                    <link rel="dns-prefetch" href="https://consent.trustarc.com" />
                    <link rel="preconnect" href={`https://${GTM_DOMAIN}`} crossOrigin="anonymous" />

                    <script>
                        {`
                        window.dataLayer = window.dataLayer || [${eventWithoutUser}];                        
                        `}
                    </script>

                    <script
                        async
                        src={`https://${GTM_DOMAIN}/gtm.js?id=${customParams?.googleTagManagerId}`}
                    ></script>
                    <script>
                        {`
                            document.addEventListener("DOMContentLoaded", (event) => {
                                var teconsentBlackbarSFCCCabled = document.querySelector('#consent_blackbar');
                                if (!teconsentBlackbarSFCCCabled) {
                                    var consent_blackbar = document.createElement('div');
                                    consent_blackbar.id = 'consent_blackbar';
                                    consent_blackbar.style.cssText =
                                        'width: 100% !important; background-color: rgba(0, 0, 0, .5) !important; position: fixed; top: 0; display: block; height: 100%; z-index: 999997;';
                                    document.querySelector('body').prepend(consent_blackbar);
                                }
                            });
                        `}
                    </script>
                    <script>
                        {`
                        (function(w, d, s, l, i) {
                            w[l] = w[l] || []  
                            w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
                            var f = d.getElementsByTagName(s)[0],
                            j = d.createElement(s),
                            dl = l != 'dataLayer' ? '&l=' + l : ''
                            j.async = true;
                            j.src = 'https://${GTM_DOMAIN}/gtm.js?id=' + i + dl
                            f.parentNode.insertBefore(j, f)
                            w.scriptAdded = true;
                        })(window, document, 'script', 'dataLayer', '${customParams?.googleTagManagerId}')
                        `}
                    </script>

                    <title>{BRAND_LABEL}</title>
                    <meta name="theme-color" content={THEME_COLOR} />
                    <meta name="apple-mobile-web-app-title" content={BRAND_LABEL} />

                    <link
                        rel="apple-touch-icon"
                        sizes="180x180"
                        href={getAssetUrl('static/icons/apple-touch-icon.png')}
                    />
                    {/* web system icon */}
                    <link
                        rel="icon"
                        type="image/png"
                        sizes="512x512"
                        href={getAssetUrl('static/icons/app-icon-512.png')}
                    />
                    <link
                        rel="icon"
                        type="image/png"
                        sizes="192x192"
                        href={getAssetUrl('static/icons/app-icon-192.png')}
                    />
                    <link rel="manifest" href={getAssetUrl('static/manifest.json')} />

                    {isHydrated() && hasKlarna && (
                        <script
                            async
                            src="https://eu-library.klarnaservices.com/lib.js"
                            data-client-id={klarnaClientId}
                        ></script>
                    )}

                    <link
                        rel="canonical"
                        href={`https://${customParams.currentHost}${buildLocalizedUrl(
                            location.pathname,
                        )}`}
                    />
                    <style type="text/css">{`.toggle-live-banner {display: none !important;}`}</style>
                </Helmet>
                <GtmPageView />
                <IntlProvider
                    onError={onErrorIntl}
                    locale={targetLocale}
                    messages={messages}
                    // For react-intl, the _default locale_ refers to the locale that the inline `defaultMessage`s are written for.
                    // NOTE: if you update this value, please also update the following npm scripts in `template-retail-react-app/package.json`:
                    // - "extract-default-translations"
                    // - "compile-translations:pseudo"
                    defaultLocale={DEFAULT_LOCALE}
                >
                    <HeaderRefContext.Provider value={headerRef}>
                        <CustomPreferencesProvider>
                            <GlobalCustomPreferencesProvider>
                                <IntegrationsInitializer>
                                    <GigyaModalProvider>
                                        <CurrencyProvider currency={currency}>
                                            <ScrollToTop />

                                            <Box
                                                id="app"
                                                display="flex"
                                                flexDirection="column"
                                                flex={1}
                                            >
                                                <SkipNavLink zIndex="skipLink">
                                                    Skip to Content
                                                </SkipNavLink>
                                                <StoreLocatorModal
                                                    isOpen={isOpenStoreLocator}
                                                    onClose={onCloseStoreLocator}
                                                />
                                                {!isCheckout &&
                                                    !isComponentsLibrary &&
                                                    !isMaintenance &&
                                                    !isSplashPageCondition &&
                                                    (showCorporateBanner ? (
                                                        <CorporateBenefitBanner
                                                            onExit={exitSource}
                                                        />
                                                    ) : (
                                                        <SovraHeader />
                                                    ))}

                                                <iframe
                                                    title="GTM"
                                                    src={`https://${GTM_DOMAIN}/ns.html?id=${customParams?.googleTagManagerId}`}
                                                    height="0"
                                                    width="0"
                                                    style={{
                                                        display: 'none',
                                                        visibility: 'hidden',
                                                    }}
                                                ></iframe>

                                                <Box
                                                    {...styles.headerWrapper}
                                                    position={
                                                        isProductDetail || isPaperless
                                                            ? 'relative'
                                                            : 'sticky'
                                                    }
                                                    ref={headerRef}
                                                >
                                                    {!isMaintenance && !isSplashPageCondition ? (
                                                        !isComponentsLibrary ? (
                                                            !isCheckout ? (
                                                                <MultisiteHeader />
                                                            ) : (
                                                                <CheckoutHeader />
                                                            )
                                                        ) : null
                                                    ) : null}
                                                </Box>
                                                {!isOnline && <OfflineBanner />}
                                                <AddToCartModalProvider>
                                                    <SkipNavContent
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            flex: 1,
                                                            outline: 0,
                                                        }}
                                                    >
                                                        <Box
                                                            as="main"
                                                            id="app-main"
                                                            role="main"
                                                            display="flex"
                                                            flexDirection="column"
                                                            flex="1"
                                                            position="relative"
                                                        >
                                                            <RecommendedPersonalizationProducts />
                                                            <OfflineBoundary isOnline={false}>
                                                                {children}
                                                            </OfflineBoundary>
                                                        </Box>
                                                    </SkipNavContent>
                                                    {!isComponentsLibrary ? <Footer /> : <></>}

                                                    {/* TODO: Empty div to prevent a console error. Remove when go live. */}
                                                    {BRAND_KEY === 'kenwood' && (
                                                        <div id="js-gigya-login-popup-container" />
                                                    )}

                                                    <AuthModal {...authModal} />
                                                </AddToCartModalProvider>
                                            </Box>
                                            <Digioh />
                                            {/* <ProfityTracker /> */}
                                            <SovendusLandingPageContainer />
                                            <SnapEngage />
                                            <GlobalGigyaModal />
                                            <DoiModal />
                                        </CurrencyProvider>
                                    </GigyaModalProvider>
                                </IntegrationsInitializer>
                            </GlobalCustomPreferencesProvider>
                        </CustomPreferencesProvider>
                    </HeaderRefContext.Provider>
                </IntlProvider>
            </StorefrontPreview>
            <script
                src={`https://consent.trustarc.com/notice?domain=${MAIN_DOMAIN}&c=teconsent&gtm=1&js=nj&text=true&noticeType=bb&language=${language}`}
                async="true"
            />
        </Box>
    )
}

App.propTypes = {
    children: PropTypes.node,
}

export default App
